import { faEdit, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import InvoiceLayoutSettingDelete from './InvoiceLayoutSettingDelete';
import { API, get } from '../../../../config';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import Spin from '../../../common/Spin';
import InvoicePreview from '../InvoiceLayoutSettings/components/InvoicePreview';

const InvoiceLayoutActionButton = ({ id, handleDelete, is_system_generated }) => {
  const [showDelete, setShowDelete] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [footer_display_option_initial, setFooter_display_option_initial] = useState([
    'BpayDetails',
    'Amex',
    'CardDetails',
    'PayIDDetails'
  ]);
  const [values, setValues] = useState();
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const params = useParams();
  const businessId = params?.business_id;
  const toggleDeleteModal = () => {
    setShowDelete(!showDelete);
  };

  const pickerTypeList = [
    {
      label: 'Plain',
      value: 0
    },
    {
      label: 'Single',
      value: 1
    },
    {
      label: 'Multiple',
      value: 2
    }
  ];

  const getInvoiceSettings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await get(`${API.INVOICE_SETTINGS}?client_business_id=${businessId}`);

      let payment_types = [];
      let footer_display = footer_display_option_initial;
      if (data?.data?.payment_options?.note === '1') {
        payment_types.push('Note');
      }
      if (data?.data?.payment_options?.coins === '1') {
        payment_types.push('Coin');
      }
      if (data?.data?.payment_options?.card === '1') {
        payment_types.push('Card');
        footer_display.push('CardDetails');
      }
      if (data?.data?.payment_options?.cheque === '1') {
        payment_types.push('Cheque');
      }
      if (data?.data?.payment_options?.eft === '1') {
        payment_types.push('EFT');
      }
      if (data?.data?.payment_options?.ffr === '1') {
        payment_types.push('FeeFromRefund');
      }
      if (data?.data?.payment_options?.gift_card === '1') {
        payment_types.push('Gift');
      }
      if (data?.data?.payment_options?.amex === '1') {
        payment_types.push('Amex');
        footer_display.push('Amex');
      }
      if (data?.data?.payment_options?.bpay === '1') {
        payment_types.push('BPay');
        footer_display.push('BpayDetails');
      }
      setPaymentTypes(payment_types);
      setLoading(false);
      setFooter_display_option_initial(footer_display);
    } catch (error) {
      setLoading(false);
      if (error?.response?.data?.errors) {
        Object.keys(error?.response?.data?.errors).forEach((key) => {
          toast.error(error?.response?.data?.errors[key][0]);
        });
        setLoading(false);
      } else {
        toast.error(error?.message);
        setLoading(false);
      }
    }
    //eslint-disable-next-line
  }, [businessId]);

  useEffect(() => {
    if (showPreview) {
      getInvoiceSettings();
    }
  }, [showPreview, getInvoiceSettings]);

  const getInvoiceTemplateSettings = useCallback(async (id) => {
    try {
      setLoading(true);
      const { data } = await get(`${API.INVOICE_SETTINGS}/${id}`);
      let intialData = data?.data?.settings;
      let clientOptions = [];
      if (intialData?.client_option?.alternateName) {
        clientOptions.push('AlternateName');
      }
      if (intialData?.client_option?.customer_ref) {
        clientOptions.push('CustomerRef');
      }
      if (intialData?.client_option?.non_individual) {
        clientOptions.push('Contact');
      }

      let invoiceDetailOption = [];
      if (intialData?.invoice_detail_option?.amount) {
        invoiceDetailOption.push('Amounts');
      }

      let subTotalOption = [];
      if (intialData?.sub_total_option?.dueDate) {
        subTotalOption.push('DueDate');
      }
      if (intialData?.sub_total_option?.term) {
        subTotalOption.push('Term');
      }

      let footerDisplayOption = [];
      if (intialData?.footer_display_option?.bpay_details) {
        footerDisplayOption.push('BpayDetails');
      }
      if (intialData?.footer_display_option?.amex) {
        footerDisplayOption.push('Amex');
      }
      if (intialData?.footer_display_option?.card_details) {
        footerDisplayOption.push('CardDetails');
      }
      if (intialData?.footer_display_option?.payID_details) {
        footerDisplayOption.push('PayIDDetails');
      }
      if (intialData?.footer_display_option?.customer_ref) {
        footerDisplayOption.push('CustomerRef');
      }

      let directBankOption = [];
      if (intialData?.direct_bank_option?.direct_debit) {
        directBankOption.push('DirectDebit');
      }

      let finalSettings = {
        name: data?.data?.settings?.name ? data?.data?.settings?.name : '',
        invoice_style: data?.data?.settings?.invoice_style === 1 ? '1' : '0',
        client_option: clientOptions,
        picker_type: data?.data?.settings?.picker_type
          ? pickerTypeList?.find((i) => i.value === data?.data?.settings?.picker_type)
          : pickerTypeList[0],
        primary_color: data?.data?.settings?.primary_color
          ? data?.data?.settings?.primary_color
          : '#FFFFFF',
        secondary_color: data?.data?.settings?.secondary_color
          ? data?.data?.settings?.secondary_color
          : '',
        font_color: data?.data?.settings?.font_color ? data?.data?.settings?.font_color : '#000000',
        invoice_detail_option: invoiceDetailOption ? invoiceDetailOption : ['Amounts'],
        gst_option: data?.data?.settings?.gst_option === 1 ? '1' : '0',
        sub_total_format_option: data?.data?.settings?.sub_total_format_option === 1 ? '1' : '0',
        sub_total_option: subTotalOption ? subTotalOption : ['DueDate', 'Term'],
        footer_display_option: footerDisplayOption
          ? footerDisplayOption
          : ['BpayDetails', 'PayIDDetails', 'CardDetails'],
        direct_bank_option: directBankOption ? directBankOption : ['DirectDebit'],
        bpayCode: data?.data?.settings?.bpay_code ? data?.data?.settings?.bpay_code : '',
        email: data?.data?.settings?.payID_email ? data?.data?.settings?.payID_email : '',
        mobile: data?.data?.settings?.payID_mobile ? data?.data?.settings?.payID_mobile : '',
        accountName: data?.data?.settings?.accountName ? data?.data?.settings?.accountName : '',
        bsb: data?.data?.settings?.bsb ? data?.data?.settings?.bsb : '',
        accountNumber: data?.data?.settings?.accountNumber
          ? data?.data?.settings?.accountNumber
          : '',
        footerText: data?.data?.settings?.footerText ? data?.data?.settings?.footerText : '',
        payment_option: paymentTypes ? paymentTypes : []
      };

      setValues(finalSettings);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error?.response?.data?.errors) {
        Object.keys(error?.response?.data?.errors).forEach((key) => {
          toast.error(error?.response?.data?.errors[key][0]);
        });
        setLoading(false);
      } else {
        toast.error(error?.message);
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id && showPreview) {
      getInvoiceTemplateSettings(id);
    }
  }, [getInvoiceTemplateSettings, id, showPreview]);
  return (
    <>
      <span>
        <Link to={`/business/${businessId}/template-settings/invoice-template/${id}`}>
          <FontAwesomeIcon icon={faEdit} size="1x" />
        </Link>
        {is_system_generated === 0 && (
          <>
            <span></span>&nbsp;&nbsp;/&nbsp;&nbsp;
            <FontAwesomeIcon
              onClick={toggleDeleteModal}
              icon={faTrash}
              size="1x"
              data-toggle="modal"
              data-target="#deleteContractor"
            />
          </>
        )}
        <>
          <span></span>&nbsp;&nbsp;/&nbsp;&nbsp;
          <FontAwesomeIcon onClick={togglePreview} icon={faEye} size="1x" data-toggle="modal" />
        </>
      </span>

      {showDelete && (
        <InvoiceLayoutSettingDelete
          id={id}
          toggleDeleteModal={toggleDeleteModal}
          handleDelete={handleDelete}
          showDelete={showDelete}
        />
      )}
      {showPreview && (
        <Modal
          size="lg"
          show={showPreview}
          onHide={togglePreview}
          dialogClassName="modal-50w larg-popup review-popup small-review"
          aria-labelledby="contained-modal-title-vcenter"
          className="business-section"
          centered
        >
          <Modal.Header className="mb-0" closeButton>
            View Invoice Layout
          </Modal.Header>
          <Modal.Body
            style={{
              padding: '15px'
            }}
          >
            {loading ? <Spin /> : <InvoicePreview type={values?.invoice_style} values={values} />}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default InvoiceLayoutActionButton;
